const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', border: 'var(--border)',
  text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
  accent: 'var(--accent)',
};
const AF = {
  h: "'Space Grotesk', system-ui, sans-serif",
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

function LegalNav() {
  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: C.bg, borderBottom: `1px solid ${C.border}`,
      height: 64, display: 'flex', alignItems: 'center',
    }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <a href="/" style={{ fontFamily: AF.h, fontSize: 20, fontWeight: 700, color: C.text, letterSpacing: '-0.5px', textDecoration: 'none' }}>
          Conjugr<span style={{ color: C.accent }}>8</span>
        </a>
        <a href="/learn-spanish" style={{ fontFamily: AF.b, fontSize: 14, color: C.muted, textDecoration: 'none' }}>
          Start learning →
        </a>
      </div>
    </nav>
  );
}

function LegalFooter() {
  return (
    <footer style={{ borderTop: `1px solid ${C.border}`, padding: '32px 24px', marginTop: 80 }}>
      <div style={{ maxWidth: 800, margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16 }}>
        <span style={{ fontFamily: AF.m, fontSize: 11, color: C.dim }}>© 2026 Conjugr8. All rights reserved.</span>
        <div style={{ display: 'flex', gap: 24 }}>
          <a href="/privacy" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Privacy</a>
          <a href="/terms" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Terms</a>
          <a href="/contact" style={{ fontFamily: AF.b, fontSize: 12, color: C.muted, textDecoration: 'none' }}>Contact</a>
        </div>
      </div>
    </footer>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 40 }}>
      <h2 style={{ fontFamily: AF.h, fontSize: 18, fontWeight: 700, color: C.text, marginBottom: 12, letterSpacing: '-0.3px' }}>{title}</h2>
      <div style={{ fontFamily: AF.b, fontSize: 15, color: C.muted, lineHeight: 1.75 }}>{children}</div>
    </section>
  );
}

export default function PrivacyPage() {
  return (
    <div style={{ background: C.bg, color: C.text, minHeight: '100vh' }}>
      <LegalNav />
      <main style={{ maxWidth: 800, margin: '0 auto', padding: '64px 24px 0' }}>
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontFamily: AF.m, fontSize: 10, color: C.accent, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 16 }}>
            Legal
          </div>
          <h1 style={{ fontFamily: AF.h, fontSize: 40, fontWeight: 700, color: C.text, letterSpacing: '-1px', marginBottom: 12 }}>
            Privacy Policy
          </h1>
          <p style={{ fontFamily: AF.b, fontSize: 14, color: C.dim }}>
            Effective date: 15 June 2026 · Last updated: 15 June 2026
          </p>
        </div>

        <Section title="Who we are">
          <p>Conjugr8 is a language-learning application operated by Ashton Spina ("we", "us", "our"). You can reach us at <a href="mailto:jobcrawls@gmail.com" style={{ color: C.accent }}>jobcrawls@gmail.com</a>.</p>
          <p style={{ marginTop: 12 }}>This policy explains what data we collect when you use conjugr8.com, how we use it, and your rights.</p>
        </Section>

        <Section title="Data we collect">
          <p><strong style={{ color: C.text }}>Account data.</strong> When you sign in with Google or another OAuth provider (via Auth0), we receive your name, email address, and profile picture. We use this to identify your account.</p>
          <p style={{ marginTop: 12 }}><strong style={{ color: C.text }}>Learning progress.</strong> Practice history, mastered verbs, CEFR level, and session statistics are stored locally in your browser (localStorage) and, when you are signed in, synced to our database so your progress persists across devices.</p>
          <p style={{ marginTop: 12 }}><strong style={{ color: C.text }}>Usage data.</strong> We collect anonymised usage statistics (pages visited, features used, session duration) via Google Analytics and Ahrefs Analytics. These tools do not use cookies for tracking.</p>
          <p style={{ marginTop: 12 }}><strong style={{ color: C.text }}>Error reports.</strong> If the app crashes, Sentry may capture a stack trace including the URL you were on and browser version. No personal data is intentionally included.</p>
          <p style={{ marginTop: 12 }}><strong style={{ color: C.text }}>AI-generated content.</strong> When you use sentence generation or text-to-speech, your input (a verb, tense, or short phrase) is sent to OpenAI's API to generate a response. OpenAI's <a href="https://openai.com/policies/privacy-policy" style={{ color: C.accent }} target="_blank" rel="noopener noreferrer">privacy policy</a> governs how they handle this data.</p>
        </Section>

        <Section title="How we use your data">
          <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>To provide and improve the learning features of Conjugr8</li>
            <li>To sync your progress across devices when you are signed in</li>
            <li>To understand how features are used so we can make them better</li>
            <li>To diagnose and fix technical errors</li>
          </ul>
          <p style={{ marginTop: 12 }}>We do not sell your data. We do not use your data for advertising.</p>
        </Section>

        <Section title="Third-party services">
          <p>We use the following services, each governed by their own privacy policy:</p>
          <ul style={{ paddingLeft: 20, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li><strong style={{ color: C.text }}>Auth0</strong> — authentication and identity management</li>
            <li><strong style={{ color: C.text }}>Amazon Web Services</strong> — cloud hosting and database</li>
            <li><strong style={{ color: C.text }}>OpenAI</strong> — AI sentence generation and text-to-speech</li>
            <li><strong style={{ color: C.text }}>Google Analytics</strong> — anonymised usage analytics</li>
            <li><strong style={{ color: C.text }}>Sentry</strong> — error monitoring</li>
          </ul>
        </Section>

        <Section title="Data retention">
          <p>Account and progress data is retained for as long as your account is active. If you delete your account, we will delete your personal data within 30 days, except where we are required to retain it for legal reasons.</p>
          <p style={{ marginTop: 12 }}>Browser-local data (localStorage) is under your control and can be cleared at any time through your browser settings.</p>
        </Section>

        <Section title="Your rights (EEA / UK residents)">
          <p>If you are in the European Economic Area or UK, you have the right to:</p>
          <ul style={{ paddingLeft: 20, marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <li>Access the personal data we hold about you</li>
            <li>Request correction of inaccurate data</li>
            <li>Request deletion of your data</li>
            <li>Object to or restrict certain processing</li>
            <li>Request a machine-readable copy of your data (data portability)</li>
            <li>Withdraw consent at any time where processing is based on consent</li>
          </ul>
          <p style={{ marginTop: 12 }}>To exercise any of these rights, email us at <a href="mailto:jobcrawls@gmail.com" style={{ color: C.accent }}>jobcrawls@gmail.com</a>. We will respond within 30 days.</p>
        </Section>

        <Section title="Cookies">
          <p>Conjugr8 uses a session cookie set by Auth0 to keep you signed in. We do not use advertising or tracking cookies. The Ahrefs Analytics script we use is cookie-free.</p>
        </Section>

        <Section title="Children">
          <p>Conjugr8 is not directed at children under 13 (or under 16 in the EEA). We do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us and we will delete it.</p>
        </Section>

        <Section title="Changes to this policy">
          <p>We may update this policy from time to time. When we do, we will update the "Last updated" date at the top of this page. Continued use of Conjugr8 after changes constitutes acceptance of the updated policy.</p>
        </Section>

        <Section title="Contact">
          <p>Questions about privacy? Email us at <a href="mailto:jobcrawls@gmail.com" style={{ color: C.accent }}>jobcrawls@gmail.com</a>.</p>
        </Section>
      </main>
      <LegalFooter />
    </div>
  );
}
